"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
/** Rounded whole rupee-style amounts (no decimals) for composition tables and charts. */
function formatCompositionAmount(value: number | string): string {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}
import { PieChart as PieIcon, Plus, Trash2, Loader2, Save } from "lucide-react";
import { getFY_YEAR } from "@/utils/sharepoint-env";

/** Appended to composition JSON in SharePoint; ignored when reading chart series. */
export const COMPOSITION_REMARKS_JSON_KEY = "_compositionRemarks";

export type CompositionChartMode = "pie" | "donut" | "column" | "line" | "bar" | "cluster";

export type CompositionClusterSeriesType = "column" | "line" | "bar";

export interface ClusterExtraSeries {
  chartKey: string;
  seriesType: CompositionClusterSeriesType;
}

export interface CompositionChartPreference {
  mode: CompositionChartMode;
  primarySeriesType: CompositionClusterSeriesType;
  extraSeries: ClusterExtraSeries[];
}

const DEFAULT_PREF: CompositionChartPreference = {
  mode: "pie",
  primarySeriesType: "column",
  extraSeries: [],
};

export interface CompositionRow {
  label: string;
  amount: number;
}

function isReservedCompositionKey(key: string): boolean {
  return key.startsWith("_");
}

function normalizeRemarksFromFile(raw: unknown): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  for (const [col, map] of Object.entries(raw as Record<string, unknown>)) {
    if (!map || typeof map !== "object" || Array.isArray(map)) continue;
    out[col] = {};
    for (const [lbl, val] of Object.entries(map as Record<string, unknown>)) {
      out[col][lbl] = String(val ?? "");
    }
  }
  return out;
}

function parseCompositionColumn(columnData: unknown): CompositionRow[] | null {
  if (Array.isArray(columnData)) {
    return columnData.map((item) => ({
      label: String((item as { label?: unknown }).label ?? ""),
      amount: Number((item as { amount?: unknown }).amount ?? 0),
    }));
  }
  if (
    columnData &&
    typeof columnData === "object" &&
    Array.isArray((columnData as { data?: unknown }).data)
  ) {
    return (columnData as { data: Array<{ label?: unknown; amount?: unknown }> }).data.map((item) => ({
      label: String(item.label ?? ""),
      amount: Number(item.amount ?? 0),
    }));
  }
  return null;
}

function scrollSectionClass() {
  return "box-border w-[min(96.8vw,880px)] max-w-[min(96.8vw,880px)] shrink-0 snap-center rounded-2xl border border-white/10 bg-gradient-to-br from-slate-950/90 via-slate-900/80 to-slate-950/90 p-4 shadow-xl shadow-black/40 ring-1 ring-white/5 sm:p-5";
}

function sanitizePrefs(p: CompositionChartPreference): CompositionChartPreference {
  const hasColumn =
    p.mode === "cluster" &&
    (p.primarySeriesType === "column" || p.extraSeries.some((s) => s.seriesType === "column"));
  if (!hasColumn) return p;
  let primarySeriesType = p.primarySeriesType;
  if (primarySeriesType === "bar") primarySeriesType = "line";
  const extraSeries = p.extraSeries.map((s) =>
    s.seriesType === "bar" ? { ...s, seriesType: "line" as const } : s,
  );
  return { ...p, primarySeriesType, extraSeries };
}

function mergeClusterRows(
  primaryKey: string,
  primaryRows: CompositionRow[],
  extras: Array<{ key: string; rows: CompositionRow[] }>,
): Record<string, string | number>[] {
  const labelSet = new Set<string>();
  primaryRows.forEach((r) => labelSet.add(r.label));
  extras.forEach((e) => e.rows.forEach((r) => labelSet.add(r.label)));
  const labels = Array.from(labelSet).sort((a, b) => a.localeCompare(b));
  return labels.map((label) => {
    const row: Record<string, string | number> = { label };
    row[primaryKey] = primaryRows.find((r) => r.label === label)?.amount ?? 0;
    extras.forEach((e) => {
      row[e.key] = e.rows.find((r) => r.label === label)?.amount ?? 0;
    });
    return row;
  });
}

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff7300",
];

function CompositionChartBlock({
  columnName,
  dataArray,
  allChartKeys,
  allDataByKey,
  prefs,
  onPrefsChange,
  remarks,
  onRemarkChange,
  isArchivalMode,
}: {
  columnName: string;
  dataArray: CompositionRow[];
  allChartKeys: string[];
  allDataByKey: Record<string, CompositionRow[]>;
  prefs: CompositionChartPreference;
  onPrefsChange: (next: CompositionChartPreference) => void;
  remarks: Record<string, string>;
  onRemarkChange: (label: string, text: string) => void;
  isArchivalMode: boolean;
}) {
  const [addSeriesNonce, setAddSeriesNonce] = useState(0);

  const totalAmount = useMemo(
    () => dataArray.reduce((sum, item) => sum + (item.amount || 0), 0),
    [dataArray],
  );

  const pieLikeData = useMemo(
    () =>
      dataArray
        .map((item) => ({
          name: item.label.length > 48 ? `${item.label.slice(0, 46)}…` : item.label,
          fullLabel: item.label,
          value: Number(item.amount || 0),
        }))
        .filter((item) => item.value > 0),
    [dataArray],
  );

  const cartesianData = useMemo(
    () =>
      dataArray.map((item) => ({
        name: item.label.length > 40 ? `${item.label.slice(0, 38)}…` : item.label,
        fullLabel: item.label,
        value: Number(item.amount || 0),
      })),
    [dataArray],
  );

  const otherKeys = useMemo(
    () => allChartKeys.filter((k) => k !== columnName),
    [allChartKeys, columnName],
  );

  const hasColumnInCluster =
    prefs.mode === "cluster" &&
    (prefs.primarySeriesType === "column" || prefs.extraSeries.some((s) => s.seriesType === "column"));

  const clusterSeriesOptions: CompositionClusterSeriesType[] = hasColumnInCluster
    ? ["column", "line"]
    : ["column", "line", "bar"];

  const setMode = (mode: CompositionChartMode) => {
    let next = { ...prefs, mode };
    if (mode !== "cluster") {
      next = { ...next, extraSeries: [], primarySeriesType: "column" };
    }
    onPrefsChange(sanitizePrefs(next));
  };

  const setPrimarySeriesType = (primarySeriesType: CompositionClusterSeriesType) => {
    onPrefsChange(sanitizePrefs({ ...prefs, primarySeriesType }));
  };

  const addExtraSeries = (chartKey: string) => {
    if (!chartKey || prefs.extraSeries.some((s) => s.chartKey === chartKey)) return;
    const defaultType: CompositionClusterSeriesType = hasColumnInCluster ? "line" : "column";
    onPrefsChange(
      sanitizePrefs({
        ...prefs,
        extraSeries: [...prefs.extraSeries, { chartKey, seriesType: defaultType }],
      }),
    );
  };

  const removeExtraSeries = (chartKey: string) => {
    onPrefsChange({
      ...prefs,
      extraSeries: prefs.extraSeries.filter((s) => s.chartKey !== chartKey),
    });
  };

  const setExtraSeriesType = (chartKey: string, seriesType: CompositionClusterSeriesType) => {
    onPrefsChange(
      sanitizePrefs({
        ...prefs,
        extraSeries: prefs.extraSeries.map((s) =>
          s.chartKey === chartKey ? { ...s, seriesType } : s,
        ),
      }),
    );
  };

  const clusterMerged = useMemo(() => {
    if (prefs.mode !== "cluster") return [];
    const extras = prefs.extraSeries
      .map((s) => {
        const rows = allDataByKey[s.chartKey];
        if (!rows) return null;
        return { key: s.chartKey, rows };
      })
      .filter(Boolean) as Array<{ key: string; rows: CompositionRow[] }>;
    return mergeClusterRows(columnName, dataArray, extras);
  }, [prefs.mode, prefs.extraSeries, columnName, dataArray, allDataByKey]);

  const clusterSeriesMeta = useMemo(() => {
    if (prefs.mode !== "cluster") return [];
    const list: Array<{ key: string; type: CompositionClusterSeriesType; color: string }> = [
      { key: columnName, type: prefs.primarySeriesType, color: COLORS[0] },
    ];
    prefs.extraSeries.forEach((s, i) => {
      list.push({ key: s.chartKey, type: s.seriesType, color: COLORS[(i + 1) % COLORS.length] });
    });
    return list;
  }, [prefs.mode, prefs.primarySeriesType, prefs.extraSeries, columnName]);

  const renderChart = () => {
    if (pieLikeData.length === 0 && prefs.mode !== "cluster") {
      return (
        <div className="w-full h-[320px] flex items-center justify-center text-white/60 border border-white/10 rounded-lg">
          No chart data available
        </div>
      );
    }

    if (prefs.mode === "pie" || prefs.mode === "donut") {
      const inner = prefs.mode === "donut" ? 55 : 0;
      return (
        <div className="h-[320px] w-full min-w-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieLikeData}
                cx="50%"
                cy="50%"
                innerRadius={inner}
                outerRadius={100}
                dataKey="value"
                nameKey="name"
              >
                {pieLikeData.map((_, index) => (
                  <Cell key={`c-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value: number | string) => [formatCompositionAmount(value), "Amount"]}
                contentStyle={{
                  background: "#0f172a",
                  border: "1px solid rgba(255,255,255,.12)",
                  color: "#fff",
                }}
                labelStyle={{ color: "#fff" }}
                itemStyle={{ color: "#fff" }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      );
    }

    if (prefs.mode === "column") {
      return (
        <div className="h-[320px] w-full min-w-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={cartesianData} margin={{ top: 8, right: 12, left: 8, bottom: 48 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} angle={-35} textAnchor="end" height={70} />
              <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} tickFormatter={(v) => formatCompositionAmount(v)} />
              <Tooltip
                formatter={(v: number | string) => formatCompositionAmount(v)}
                contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,.12)" }}
              />
              <Bar dataKey="value" name="Amount" fill={COLORS[0]} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      );
    }

    if (prefs.mode === "bar") {
      return (
        <div className="h-[320px] w-full min-w-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart layout="vertical" data={cartesianData} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 11 }} tickFormatter={(v) => formatCompositionAmount(v)} />
              <YAxis type="category" dataKey="name" width={120} tick={{ fill: "#94a3b8", fontSize: 10 }} />
              <Tooltip
                formatter={(v: number | string) => formatCompositionAmount(v)}
                contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,.12)" }}
              />
              <Bar dataKey="value" name="Amount" fill={COLORS[1]} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      );
    }

    if (prefs.mode === "line") {
      return (
        <div className="h-[320px] w-full min-w-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={cartesianData} margin={{ top: 8, right: 12, left: 8, bottom: 48 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} angle={-35} textAnchor="end" height={70} />
              <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} tickFormatter={(v) => formatCompositionAmount(v)} />
              <Tooltip
                formatter={(v: number | string) => formatCompositionAmount(v)}
                contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,.12)" }}
              />
              <Line type="monotone" dataKey="value" stroke={COLORS[2]} strokeWidth={2} dot={{ r: 3 }} name="Amount" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      );
    }

    if (prefs.mode === "cluster") {
      if (clusterMerged.length === 0) {
        return (
          <div className="w-full h-[320px] flex items-center justify-center text-white/60 border border-white/10 rounded-lg">
            No data for cluster chart
          </div>
        );
      }
      return (
        <div className="h-[360px] w-full min-w-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={clusterMerged} margin={{ top: 8, right: 12, left: 8, bottom: 56 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis
                dataKey="label"
                tick={{ fill: "#94a3b8", fontSize: 9 }}
                angle={-35}
                textAnchor="end"
                height={72}
                interval={0}
              />
              <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} tickFormatter={(v) => formatCompositionAmount(v)} />
              <Tooltip
                formatter={(v: number | string) => formatCompositionAmount(v)}
                contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,.12)" }}
              />
              <Legend />
              {clusterSeriesMeta.map((meta) => {
                if (meta.type === "column") {
                  return (
                    <Bar
                      key={meta.key}
                      dataKey={meta.key}
                      name={meta.key}
                      fill={meta.color}
                      radius={[3, 3, 0, 0]}
                      maxBarSize={40}
                    />
                  );
                }
                if (meta.type === "bar") {
                  return (
                    <Bar
                      key={meta.key}
                      dataKey={meta.key}
                      name={`${meta.key} (bar)`}
                      fill={meta.color}
                      fillOpacity={0.85}
                      radius={[2, 2, 0, 0]}
                      maxBarSize={28}
                    />
                  );
                }
                return (
                  <Line
                    key={meta.key}
                    type="monotone"
                    dataKey={meta.key}
                    name={meta.key}
                    stroke={meta.color}
                    strokeWidth={2}
                    dot={{ r: 2 }}
                  />
                );
              })}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      );
    }

    return null;
  };

  const availableToAdd = otherKeys.filter((k) => !prefs.extraSeries.some((s) => s.chartKey === k));

  return (
    <section className={scrollSectionClass()}>
      <div className="mb-4 flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-lg font-semibold tracking-tight text-white">
            <PieIcon className="h-5 w-5 shrink-0 text-cyan-400" />
            {columnName}
          </h3>
          <p className="mt-1 text-sm text-slate-400">From loaded composition JSON</p>
        </div>
      </div>

      <div className="space-y-3 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5 min-w-[160px]">
            <Label className="text-white/80 text-xs">Chart type</Label>
            <Select
              value={prefs.mode}
              onValueChange={(v) => setMode(v as CompositionChartMode)}
              disabled={isArchivalMode}
            >
              <SelectTrigger className="w-[200px] border-white/10 bg-black/40 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-black/95 text-white">
                <SelectItem value="pie">Pie chart</SelectItem>
                <SelectItem value="donut">Donut chart</SelectItem>
                <SelectItem value="column">Column chart</SelectItem>
                <SelectItem value="line">Line chart</SelectItem>
                <SelectItem value="bar">Bar graph</SelectItem>
                <SelectItem value="cluster">Cluster chart</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {prefs.mode === "cluster" && (
            <div className="space-y-1.5 min-w-[180px]">
              <Label className="text-white/80 text-xs">This chart (primary)</Label>
              <Select
                value={prefs.primarySeriesType}
                onValueChange={(v) => setPrimarySeriesType(v as CompositionClusterSeriesType)}
                disabled={isArchivalMode}
              >
                <SelectTrigger className="w-[180px] border-white/10 bg-black/40 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-black/95 text-white">
                  {clusterSeriesOptions.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt === "column" ? "Column" : opt === "line" ? "Line" : "Bar"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {prefs.mode === "cluster" && (
          <div className="rounded-lg border border-white/10 bg-black/30 p-3 space-y-3">
            <p className="text-xs text-white/60">
              <span className="text-cyan-300 font-medium">{columnName}</span> is always included. Add other charts to
              combine in one graph. If any series uses a column chart, bar graph is not available.
            </p>
            {prefs.extraSeries.map((s) => (
              <div key={s.chartKey} className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-white/90 w-40 truncate" title={s.chartKey}>
                  {s.chartKey}
                </span>
                <Select
                  value={s.seriesType}
                  onValueChange={(v) => setExtraSeriesType(s.chartKey, v as CompositionClusterSeriesType)}
                  disabled={isArchivalMode}
                >
                  <SelectTrigger className="w-[140px] border-white/10 bg-black/50 text-white h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/95 text-white">
                    {clusterSeriesOptions.map((opt) => (
                      <SelectItem key={opt} value={opt}>
                        {opt === "column" ? "Column" : opt === "line" ? "Line" : "Bar"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-red-400 hover:text-red-300"
                  onClick={() => removeExtraSeries(s.chartKey)}
                  disabled={isArchivalMode}
                  aria-label={`Remove ${s.chartKey}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {availableToAdd.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Select
                  key={addSeriesNonce}
                  onValueChange={(v) => {
                    addExtraSeries(v);
                    setAddSeriesNonce((n) => n + 1);
                  }}
                  disabled={isArchivalMode}
                >
                  <SelectTrigger className="w-[280px] border-dashed border-white/20 bg-black/30 text-white h-9">
                    <Plus className="h-4 w-4 mr-2 shrink-0 opacity-70" />
                    <SelectValue placeholder="Add chart to cluster…" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/95 text-white">
                    {availableToAdd.map((k) => (
                      <SelectItem key={k} value={k}>
                        {k}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="min-w-0 max-w-full overflow-x-auto pb-2">{renderChart()}</div>

      <div className="mt-6 overflow-x-auto">
        <table className="w-full min-w-[520px] border-collapse border border-white/20 text-sm">
          <thead>
            <tr className="bg-black/60">
              <th className="border border-white/20 px-3 py-2 text-left text-white font-semibold">Label</th>
              <th className="border border-white/20 px-3 py-2 text-right text-white font-semibold">Amount</th>
              <th className="border border-white/20 px-3 py-2 text-right text-white font-semibold">Percentage</th>
              <th className="border border-white/20 px-3 py-2 text-left text-white font-semibold min-w-[180px]">
                Remarks
              </th>
            </tr>
          </thead>
          <tbody>
            {dataArray.map((item, idx) => {
              const pct =
                totalAmount > 0 ? (((item.amount || 0) / totalAmount) * 100).toFixed(2) : "0.00";
              const rk = item.label;
              return (
                <tr key={`${rk}-${idx}`} className="hover:bg-white/5">
                  <td className="border border-white/20 px-3 py-2 text-white align-top">{item.label}</td>
                  <td className="border border-white/20 px-3 py-2 text-white text-right align-top">
                    {formatCompositionAmount(item.amount || 0)}
                  </td>
                  <td className="border border-white/20 px-3 py-2 text-white text-right align-top">{pct}%</td>
                  <td className="border border-white/20 px-3 py-1 align-top">
                    <Textarea
                      value={remarks[rk] ?? ""}
                      onChange={(e) => onRemarkChange(rk, e.target.value)}
                      placeholder="Remarks"
                      disabled={isArchivalMode}
                      className="min-h-[52px] border-white/10 bg-black/40 text-white text-xs resize-y"
                      rows={2}
                    />
                  </td>
                </tr>
              );
            })}
            <tr className="bg-black/40 font-semibold">
              <td className="border border-white/20 px-3 py-2 text-white">Total</td>
              <td className="border border-white/20 px-3 py-2 text-white text-right">
                {formatCompositionAmount(totalAmount)}
              </td>
              <td className="border border-white/20 px-3 py-2 text-white text-right">100%</td>
              <td className="border border-white/20 px-3 py-2" />
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

export interface CompositionAnalysisChartsProps {
  loadedCompositionData: Record<string, unknown>;
  resultsHeading: string;
  isArchivalMode: boolean;
  /** Used for persisting chart type + remarks in localStorage */
  storageKey: string;
  /** juggernaut JSON file name (required to save remarks to SharePoint) */
  outputJsonFileName?: string;
  phaseFormatted?: string;
  workspacePrefix?: string;
}

export function CompositionAnalysisCharts({
  loadedCompositionData,
  resultsHeading,
  isArchivalMode,
  storageKey,
  outputJsonFileName,
  phaseFormatted = "Execution",
  workspacePrefix = "",
}: CompositionAnalysisChartsProps) {
  const parsed = useMemo(() => {
    const entries: { key: string; rows: CompositionRow[] }[] = [];
    for (const [key, columnData] of Object.entries(loadedCompositionData)) {
      if (isReservedCompositionKey(key)) continue;
      const rows = parseCompositionColumn(columnData);
      if (rows && rows.length > 0) entries.push({ key, rows });
    }
    return entries;
  }, [loadedCompositionData]);

  const chartDataSignature = useMemo(() => {
    const copy: Record<string, unknown> = { ...loadedCompositionData };
    delete copy[COMPOSITION_REMARKS_JSON_KEY];
    try {
      return JSON.stringify(copy);
    } catch {
      return String(Object.keys(copy).sort().join("|"));
    }
  }, [loadedCompositionData]);

  const allDataByKey = useMemo(() => {
    const m: Record<string, CompositionRow[]> = {};
    parsed.forEach((e) => {
      m[e.key] = e.rows;
    });
    return m;
  }, [parsed]);

  const chartKeys = useMemo(() => parsed.map((e) => e.key), [parsed]);

  const prefsStorageKey = `${storageKey}::composition_chart_prefs`;
  const remarksStorageKey = `${storageKey}::composition_remarks`;

  const [prefsMap, setPrefsMap] = useState<Record<string, CompositionChartPreference>>({});
  const [remarksMap, setRemarksMap] = useState<Record<string, Record<string, string>>>({});
  const [remarksSaveStatus, setRemarksSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [remarksSaveError, setRemarksSaveError] = useState<string | null>(null);

  const chartKeysSig = useMemo(() => chartKeys.slice().sort().join("|"), [chartKeys]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(prefsStorageKey);
      if (raw) {
        const parsedPrefs = JSON.parse(raw) as Record<string, CompositionChartPreference>;
        setPrefsMap(parsedPrefs || {});
      }
    } catch {
      /* ignore */
    }
  }, [prefsStorageKey]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(remarksStorageKey);
      if (raw) {
        const parsedRemarks = JSON.parse(raw) as Record<string, Record<string, string>>;
        setRemarksMap(parsedRemarks || {});
      }
    } catch {
      /* ignore */
    }
  }, [remarksStorageKey]);

  useEffect(() => {
    const fromFile = loadedCompositionData[COMPOSITION_REMARKS_JSON_KEY];
    if (fromFile !== undefined && fromFile !== null) {
      const normalized = normalizeRemarksFromFile(fromFile);
      setRemarksMap(normalized);
      try {
        localStorage.setItem(remarksStorageKey, JSON.stringify(normalized));
      } catch {
        /* ignore */
      }
    }
  }, [chartDataSignature, remarksStorageKey, loadedCompositionData]);

  useEffect(() => {
    setPrefsMap((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const k of chartKeys) {
        if (!next[k]) {
          next[k] = { ...DEFAULT_PREF };
          changed = true;
        }
      }
      Object.keys(next).forEach((k) => {
        if (!chartKeys.includes(k)) {
          delete next[k];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [chartKeysSig, chartKeys]);

  const updatePref = useCallback(
    (columnKey: string, pref: CompositionChartPreference) => {
      setPrefsMap((prev) => {
        const next = { ...prev, [columnKey]: sanitizePrefs(pref) };
        try {
          localStorage.setItem(prefsStorageKey, JSON.stringify(next));
        } catch {
          /* ignore */
        }
        return next;
      });
    },
    [prefsStorageKey],
  );

  const updateRemark = useCallback(
    (columnKey: string, label: string, text: string) => {
      setRemarksMap((prev) => {
        const col = { ...(prev[columnKey] || {}) };
        col[label] = text;
        const next = { ...prev, [columnKey]: col };
        try {
          localStorage.setItem(remarksStorageKey, JSON.stringify(next));
        } catch {
          /* ignore */
        }
        return next;
      });
    },
    [remarksStorageKey],
  );

  const saveRemarksToJsonFile = useCallback(async () => {
    if (!outputJsonFileName) {
      setRemarksSaveError("Output file name is not configured");
      setRemarksSaveStatus("error");
      return;
    }
    const fyYear = getFY_YEAR();
    if (!fyYear) {
      setRemarksSaveError("FY year is not configured. Select an engagement first.");
      setRemarksSaveStatus("error");
      return;
    }
    const upload = (window as unknown as { sharePointAPI?: { uploadFile?: (opts: unknown) => Promise<{ success?: boolean; error?: string }> } })
      .sharePointAPI?.uploadFile;
    if (!upload) {
      setRemarksSaveError("SharePoint upload is not available");
      setRemarksSaveStatus("error");
      return;
    }

    setRemarksSaveStatus("saving");
    setRemarksSaveError(null);

    try {
      let baseDoc: Record<string, unknown>;
      try {
        baseDoc = JSON.parse(JSON.stringify(loadedCompositionData)) as Record<string, unknown>;
      } catch {
        baseDoc = { ...loadedCompositionData };
      }

      const remarksPayload: Record<string, Record<string, string>> = {};
      for (const col of chartKeys) {
        remarksPayload[col] = { ...(remarksMap[col] || {}) };
      }
      baseDoc[COMPOSITION_REMARKS_JSON_KEY] = remarksPayload;

      const jsonString = JSON.stringify(baseDoc, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const sectionStem = outputJsonFileName.replace(/\.json$/i, "");
      const ref =
        workspacePrefix.trim().length > 0
          ? `${phaseFormatted} ${workspacePrefix} Composition analysis`
          : `${phaseFormatted} Composition analysis`;

      const result = await upload({
        fileContent: base64Content,
        fileName: outputJsonFileName,
        referenceValue: ref,
        folderName: "juggernaut",
        fyYear,
        sectionList: [sectionStem],
      });

      if (!result?.success) {
        throw new Error(result?.error || "Upload failed");
      }

      setRemarksSaveStatus("saved");
      setTimeout(() => setRemarksSaveStatus("idle"), 3500);
    } catch (e) {
      setRemarksSaveStatus("error");
      setRemarksSaveError(e instanceof Error ? e.message : "Failed to save remarks");
    }
  }, [
    outputJsonFileName,
    loadedCompositionData,
    remarksMap,
    chartKeys,
    phaseFormatted,
    workspacePrefix,
  ]);

  if (parsed.length === 0) {
    return (
      <div className="space-y-2 mt-6 pt-6 border-t border-white/10">
        <h3 className="text-xl font-semibold text-white">{resultsHeading}</h3>
        <p className="text-sm text-white/60">
          No chart data loaded. Click &quot;Load Charts&quot; to download results from SharePoint.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-6 pt-6 border-t border-white/10">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-xl font-semibold text-white">{resultsHeading}</h3>
        <div className="flex flex-wrap items-center gap-2">
          {remarksSaveStatus === "saved" && (
            <span className="text-sm text-green-400">Remarks saved to JSON file.</span>
          )}
          {remarksSaveStatus === "error" && remarksSaveError && (
            <span className="text-sm text-red-400 max-w-[280px] truncate" title={remarksSaveError}>
              {remarksSaveError}
            </span>
          )}
          <Button
            type="button"
            onClick={() => void saveRemarksToJsonFile()}
            disabled={
              isArchivalMode ||
              remarksSaveStatus === "saving" ||
              !outputJsonFileName ||
              chartKeys.length === 0
            }
            className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
          >
            {remarksSaveStatus === "saving" ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save remarks
              </>
            )}
          </Button>
        </div>
      </div>
      <div className="relative -mx-1 sm:-mx-0">
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-6 bg-gradient-to-r from-black to-transparent sm:w-8" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-6 bg-gradient-to-l from-black to-transparent sm:w-8" />
        <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-px-4 scroll-py-1 pb-4 pt-1 px-3 sm:gap-5 sm:scroll-px-6 sm:px-4 [scrollbar-width:thin] [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/10">
          {parsed.map(({ key, rows }) => (
            <CompositionChartBlock
              key={key}
              columnName={key}
              dataArray={rows}
              allChartKeys={chartKeys}
              allDataByKey={allDataByKey}
              prefs={prefsMap[key] ?? DEFAULT_PREF}
              onPrefsChange={(p) => updatePref(key, p)}
              remarks={remarksMap[key] ?? {}}
              onRemarkChange={(label, text) => updateRemark(key, label, text)}
              isArchivalMode={isArchivalMode}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
